import { environment } from '../../../environments/environment';
import { Base64 } from 'js-base64';
import * as Sentry from '@sentry/angular';

// Função para obter o usuário atual
const getCurrentUser = () => {
    try {
        const userStr = localStorage.getItem('currentUser');
        if (userStr) {
            const user = JSON.parse(userStr);
            return {
                email: Base64.decode(user.email),
            };
        }
        return null;
    } catch {
        return null;
    }
};

// Função para verificar se é erro de manutenção
const isMaintenanceError = (event: any): boolean => {
    // Verifica mensagens de erro relacionadas a manutenção
    const exception = event.exception?.values?.[0];
    if (exception) {
        const errorMessage = exception.value || '';
        if (errorMessage.includes('Maintenance mode')) {
            return true;
        }
    }

    // Verifica se é erro HTTP 503 com maintenance: true
    const extra = event.extra || {};
    const xhrResponse = extra.xhr_response;
    if (xhrResponse) {
        if (xhrResponse.status === 503) {
            try {
                const responseBody = typeof xhrResponse.response === 'string'
                    ? JSON.parse(xhrResponse.response)
                    : xhrResponse.response;
                if (responseBody && responseBody.maintenance === true) {
                    return true;
                }
            } catch {
                // Se não conseguir fazer parse, continua
            }
        }
    }

    // Verifica no contexto do erro
    const contexts = event.contexts || {};
    const response = contexts.response;
    if (response && response.status_code === 503) {
        const body = response.body;
        if (body && typeof body === 'object' && body.maintenance === true) {
            return true;
        }
    }

    return false;
};

// Função para ignorar se um erro é de uma biblioteca de terceiros
const isThirdPartyError = (event: any): boolean => {

    const exception = event.exception?.values?.[0];
    if (exception) {
        const errorMessage = exception.value || '';
        const errorType = exception.type || '';
        if (errorMessage.includes('text/html') ||
            errorMessage.includes('primeng') ||
            errorMessage.includes('@angular') ||
            errorMessage.includes('node_modules') ||
            errorType.includes('primeng')) {
            return true;
        }
    }

    const stacktrace = event.exception?.values?.[0]?.stacktrace;
    if (stacktrace?.frames) {
        return stacktrace.frames.some(
            frame => {
                const filename = frame.filename || '';
                return filename.includes('text/html') ||
                       filename.includes('node_modules') ||
                       filename.includes('service-worker') ||
                       filename.includes('app.js') ||
                       filename.includes('pwa.js') ||
                       filename.includes('primeng') ||
                       filename.includes('primeng-calendar');
            }
        );
    }

    return false;
};

// Função para formatar o tipo de erro
const formatErrorType = (event: any): void => {

    if (event.exception) {
        const error = event.exception.values[0];
        if (error.type === 'TypeError') {
            if (error.value.includes('undefined (reading')) {
                const prop = error.value.match(/reading '(.+)'/)?.[1];
                error.type = `Propriedade Indefinida: ${prop}`;
            } else if (error.value.includes('null (reading')) {
                const prop = error.value.match(/reading '(.+)'/)?.[1];
                error.type = `Propriedade Nula: ${prop}`;
            } else if (error.value.includes('is not a function')) {
                const func = error.value.match(/(.+) is not a function/)?.[1];
                error.type = `Função Inválida: ${func}`;
            } else if (error.value.includes('is not a valid')) {
                error.type = `Tipo Inválido: ${error.value}`;
            }
        }
    }
};

// Função para adicionar informações adicionais ao evento
const addEventInfo = (event: any): void => {

    // Captura informações da requisição XHR se disponível
    const xhr = (event as any).extra?.xhr;
    if (xhr) {
        event.extra = {
            ...event.extra,
            xhr_response: {
                status: xhr.status,
                response: xhr.response,
                headers: xhr.getAllResponseHeaders()
            }
        };
    }

    // Adiciona informações do usuário ao evento
    const user = getCurrentUser();
    if (user) {
        event.user = user;
    }
};

// Função para carregar a versão do version.json de forma assíncrona
const loadVersionFromFile = (): void => {

    if(!environment.production) {
        // Não carrega versão em ambiente de desenvolvimento
        return;
    }

    // Executa de forma assíncrona para não bloquear a inicialização
    setTimeout(() => {
        try {
            fetch('/version.json?t=' + new Date().getTime())
                .then(response => {
                    if (!response.ok) {
                        return null;
                    }
                    return response.json();
                })
                .then((versionData: any) => {
                    if (versionData && versionData.version) {
                        const release = `foodchecker@${versionData.version}`;

                        // Define o release como tag (forma mais compatível)
                        Sentry.setTag('release', release);
                        Sentry.setTag('app_version', versionData.version);

                        // Adiciona informações adicionais como tags
                        if (versionData.hash) {
                            Sentry.setTag('build_hash', versionData.hash);
                        }
                        if (versionData.buildDate) {
                            Sentry.setTag('build_date', versionData.buildDate);
                        }

                        // Tenta definir o release em contexto (API moderna)
                        try {
                            if (typeof (Sentry as any).setContext === 'function') {
                                (Sentry as any).setContext('app', {
                                    version: versionData.version,
                                    release: release,
                                    ...(versionData.hash && { hash: versionData.hash }),
                                    ...(versionData.buildDate && { buildDate: versionData.buildDate })
                                });
                            }
                        } catch {
                            // Silenciosamente ignora se a API não estiver disponível
                            // O release já foi definido como tag acima
                        }
                    }
                })
                .catch(() => {
                    // Silenciosamente ignora erros ao carregar version.json
                    // O Sentry funcionará normalmente sem release definido
                });
        } catch {
            // Silenciosamente ignora erros
        }
    }, 0);
};

// Função para inicializar o Sentry
export const initSentry = (): void => {
    const localeId = localStorage.getItem('language') ? localStorage.getItem('language').replace('_', '-') : 'pt-BR';

    Sentry.init({
        dsn: environment.SENTRY_DSN,
        enabled: environment.production,
        environment: environment.production ? 'production' : 'development',
        // release será definido de forma assíncrona após carregar version.json
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                networkDetailAllowUrls: [environment.API_URL],
                networkCaptureBodies: true,
                networkRequestHeaders: ['content-type', 'accept'],
                networkResponseHeaders: ['content-type'],
                maskAllText: false,
                blockAllMedia: false,
            }),
            Sentry.dedupeIntegration()
        ],
        beforeSend(event) {

            // Ignora erros de manutenção
            if (isMaintenanceError(event)) {
                return null;
            }

            if (isThirdPartyError(event)) {
                return null;
            }

            formatErrorType(event);

            addEventInfo(event);

            return event;
        },
        initialScope: {
            user: getCurrentUser(),
            tags: {
                locale: localeId,
            }
        },
        tracesSampleRate: environment.production ? 0.2 : 0.1,
        replaysSessionSampleRate: environment.production ? 0.1 : 0.0,
        replaysOnErrorSampleRate: 1.0,
        tracePropagationTargets: ['localhost', environment.API_URL],
        ignoreErrors: [
            /Token has expired/,
            /token could not be parsed/,
            /Maintenance mode/,
            /Maintenance mode active/,
        ],
    });

    // Carrega versão do version.json de forma assíncrona (não bloqueia)
    loadVersionFromFile();
};
